// Phase: "cancelled" — navigation was cancelled; prompts for Return to Base
// or Choose Another Location (cancelling never auto-decides what's next).
import React from "react";
import { Box, Stack, Avatar, Typography, Button } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import NavigationIcon from "@mui/icons-material/Navigation";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PropTypes from "prop-types";

import DeliveryLayout from "../../../components/delivery/DeliveryLayout";
import SectionCard from "../../../components/delivery/SectionCard";
import ActionButtonRow from "../../../components/delivery/ActionButtonRow";
import DoorControl from "../../../components/delivery/DoorControl";
import { actionBtnSx, getDividerSx } from "../deliveryStyles";
import { tokens } from "../../../theme";

const CancelledPhase = ({
  selectedMap,
  navigationTarget,
  robot,
  fromRobotId,
  batteryLevel,
  batteryCharging,
  showMapSelectionModalDialog,
  closeMapSelectionDialogModal,
  portalRobots,
  onRobotChange,
  onEmergencyResume,
  isRobotConnected,
  isDoorOpen,
  onDoorPress,
  isAeroRobot,
  cancelReturnPoint,
  onBackToSelection,
  onReturnToBase,
}) => (
  <DeliveryLayout
    selectedMap={selectedMap}
    navigationTarget={navigationTarget}
    robot={robot}
    fromRobotId={fromRobotId}
    batteryLevel={batteryLevel}
    batteryCharging={batteryCharging}
    showMapSelectionModalDialog={showMapSelectionModalDialog}
    closeMapSelectionDialogModal={closeMapSelectionDialogModal}
    portalRobots={portalRobots}
    onRobotChange={onRobotChange}
    onEmergencyResume={onEmergencyResume}
    isRobotConnected={isRobotConnected}
    rightPanel={
      <SectionCard
        icon={
          <Avatar
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
            }}
          >
            <CancelIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Avatar>
        }
        title="Navigation Cancelled"
        subtitle="The navigation task was cancelled"
        headerBg="linear-gradient(135deg, #3b0000 0%, #7f1d1d 100%)"
      >
        <DoorControl isDoorOpen={isDoorOpen} onDoorPress={onDoorPress} isAeroRobot={isAeroRobot} />
        {isAeroRobot && <Box sx={getDividerSx()} />}

        {/* Cancellation explanation */}
        <Box
          sx={{
            p: 2,
            background: tokens.dangerSoft,
            border: `1px solid ${tokens.danger}`,
            borderRadius: "12px",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.danger, mb: 0.5 }}>
            What happened?
          </Typography>
          <Typography sx={{ fontSize: 12, color: tokens.text2, lineHeight: 1.6 }}>
            {cancelReturnPoint
              ? `Navigation was cancelled and the robot is heading back to ${cancelReturnPoint.name ?? "a safe point"}.`
              : "Navigation was cancelled and the robot has stopped where it is. Choose what it should do next."}
          </Typography>
        </Box>

        {/* Return point info (shown when available) */}
        {cancelReturnPoint && (
          <Box
            sx={{
              p: 1.5,
              background: tokens.accentSoft,
              border: `1px solid ${tokens.accent}`,
              borderRadius: "10px",
            }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <NavigationIcon sx={{ fontSize: 15, color: tokens.accent }} />
              <Typography sx={{ fontSize: 12, color: tokens.accent }}>
                Return point:{" "}
                <strong>
                  {cancelReturnPoint.name ??
                    `(${cancelReturnPoint.x?.toFixed(1)}, ${cancelReturnPoint.y?.toFixed(1)})`}
                </strong>
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Return to Base / Choose Another Location */}
        <ActionButtonRow>
          <Button
            variant="contained"
            onClick={onReturnToBase}
            startIcon={<HomeIcon sx={{ fontSize: "16px !important" }} />}
            sx={{
              ...actionBtnSx,
              whiteSpace: "nowrap",
              background: tokens.success,
              color: "#fff",
              "&:hover": { background: tokens.success },
            }}
          >
            Return to Base
          </Button>
          <Button
            variant="outlined"
            onClick={onBackToSelection}
            startIcon={<LocationOnIcon sx={{ fontSize: "16px !important" }} />}
            sx={{ ...actionBtnSx, whiteSpace: "nowrap" }}
          >
            Choose Location
          </Button>
        </ActionButtonRow>
      </SectionCard>
    }
  />
);

CancelledPhase.propTypes = {
  selectedMap: PropTypes.object,
  navigationTarget: PropTypes.object,
  robot: PropTypes.object,
  fromRobotId: PropTypes.string,
  batteryLevel: PropTypes.number,
  batteryCharging: PropTypes.bool,
  showMapSelectionModalDialog: PropTypes.bool,
  closeMapSelectionDialogModal: PropTypes.func,
  portalRobots: PropTypes.array,
  onRobotChange: PropTypes.func,
  onEmergencyResume: PropTypes.func,
  isRobotConnected: PropTypes.bool,
  isDoorOpen: PropTypes.bool,
  onDoorPress: PropTypes.func,
  isAeroRobot: PropTypes.bool,
  cancelReturnPoint: PropTypes.object,
  onBackToSelection: PropTypes.func,
  onReturnToBase: PropTypes.func,
};

export default CancelledPhase;
